/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import {
	useEffect,
	useRef,
	useState,
	type ComponentType,
	type FC,
	type ReactNode,
	type SVGProps,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { Sparkles, X } from "lucide-react";

const ACCENT = "#f09a38";
const PRIMARY = "#3b0092";
const GLOW = "240, 154, 56";

/* ------------------------------------------------------------------ */
/*  Public types                                                       */
/* ------------------------------------------------------------------ */

export type ShowcaseIcon = ComponentType<SVGProps<SVGSVGElement>>;

export interface ShowcaseHighlight {
	icon: ShowcaseIcon;
	title: string;
	body: string;
}

export interface ShowcaseSpec {
	icon: ShowcaseIcon;
	feature: string;
	capability: string;
}

export interface AcheShowcaseProps {
	/** Small eyebrow above the title. */
	eyebrow?: string;
	/** Main section title — accepts JSX so callers can style parts. */
	title: ReactNode;
	/** Lead paragraph next to the visual — accepts JSX. */
	description: ReactNode;

	/** Image cycle for the hero card. First one is shown by default. */
	images: string[];
	/** Decorative italic wordmark in the top-left of the hero card. */
	wordmarkPrimary?: string;
	/** Decorative italic wordmark in the bottom-right of the hero card. */
	wordmarkSecondary?: string;
	/** Optional pill in the top-right of the hero card (e.g. compliance badges). */
	compliancePill?: string;
	/** Small uppercase caption above the hero title. */
	captionEyebrow?: string;
	/** Larger caption text in the bottom-left of the hero card. */
	captionTitle?: string;
	/** Alt text for the hero/thumbnail images. */
	imageAlt?: string;

	/** Stacked highlight rows (icon left, copy right). */
	highlights: ShowcaseHighlight[];

	/** Eyebrow above the specs table. */
	specsEyebrow?: string;
	/** Title above the specs table. */
	specsTitle?: string;
	/** Specs table rows. */
	specs: ShowcaseSpec[];
	/** Label shown in the bottom-left of the specs table footer. */
	specsFooterLabel?: string;
}

/* ------------------------------------------------------------------ */
/*  Hero tilt card                                                     */
/* ------------------------------------------------------------------ */

interface TiltShowcaseCardProps {
	image: string;
	imageAlt: string;
	wordmarkPrimary: string;
	wordmarkSecondary: string;
	compliancePill?: string;
	captionEyebrow?: string;
	captionTitle?: string;
	onImageClick: () => void;
}

const TiltShowcaseCard: FC<TiltShowcaseCardProps> = ({
	image,
	imageAlt,
	wordmarkPrimary,
	wordmarkSecondary,
	compliancePill,
	captionEyebrow,
	captionTitle,
	onImageClick,
}) => {
	const tiltRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const el = tiltRef.current;
		if (!el) return;

		const ease = "cubic-bezier(.03,.98,.52,.99)";
		const MAX = 14;

		const handleMove = (e: MouseEvent) => {
			const rect = el.getBoundingClientRect();
			const px = (e.clientX - rect.left) / rect.width;
			const py = (e.clientY - rect.top) / rect.height;
			const rotateY = (px - 0.5) * 2 * MAX;
			const rotateX = (0.5 - py) * 2 * MAX;

			el.style.setProperty("--glow-x", `${px * 100}%`);
			el.style.setProperty("--glow-y", `${py * 100}%`);
			el.style.setProperty("--glow-intensity", "1");

			gsap.to(el, {
				rotateX,
				rotateY,
				scale: 1.02,
				duration: 0.3,
				ease,
				transformPerspective: 1200,
				transformOrigin: "center",
			});
		};

		const handleLeave = () => {
			el.style.setProperty("--glow-intensity", "0");
			gsap.to(el, {
				rotateX: 0,
				rotateY: 0,
				scale: 1,
				duration: 0.6,
				ease,
			});
		};

		el.addEventListener("mousemove", handleMove);
		el.addEventListener("mouseleave", handleLeave);
		return () => {
			el.removeEventListener("mousemove", handleMove);
			el.removeEventListener("mouseleave", handleLeave);
		};
	}, []);

	return (
		<motion.div
			ref={tiltRef}
			initial="rest"
			animate="rest"
			whileHover="hover"
			className="ache-tilt relative h-full min-h-100 sm:min-h-120 w-full rounded-[28px] overflow-hidden cursor-zoom-in border border-slate-200 shadow-[0_18px_50px_rgba(15,23,42,0.08)]"
			onClick={onImageClick}
			style={{
				transformStyle: "preserve-3d",
				background:
					"radial-gradient(ellipse at 30% 20%, #ffffff 0%, #f8fafc 55%, #eef2f7 100%)",
				["--glow-x" as string]: "50%",
				["--glow-y" as string]: "50%",
				["--glow-intensity" as string]: "0",
			}}>
	
		

	

			{/* Product image */}
			<motion.img
				key={image}
				src={image}
				alt={imageAlt}
				className="absolute left-1/2 top-1/2 z-10 h-[95%] w-[95%] -translate-x-1/2 -translate-y-1/2 object-contain drop-shadow-[0_30px_40px_rgba(59,0,146,0.18)]"
				style={{ transformStyle: "preserve-3d" }}
				initial={{ opacity: 0, scale: 0.92 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
				onError={(e) => {
					(e.currentTarget as HTMLImageElement).src = "/noimg.png";
				}}
			/>

		


		
		</motion.div>
	);
};

/* ------------------------------------------------------------------ */
/*  Main section                                                       */
/* ------------------------------------------------------------------ */

const AcheShowcase: FC<AcheShowcaseProps> = ({
	eyebrow = "Engineering Excellence & Design",
	title,
	description,
	images,
	wordmarkPrimary = "PRODUCT",
	wordmarkSecondary = "POSITRON",
	compliancePill,
	captionEyebrow,
	captionTitle,
	imageAlt = "Product",
	highlights,
	specsTitle = "Performance Specifications",
	specsEyebrow = "Engineered Capability",
	specs,
	specsFooterLabel,
}) => {
	const { t } = useTranslation();
	const [activeImage, setActiveImage] = useState(0);
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const safeIndex = Math.min(activeImage, Math.max(images.length - 1, 0));

	useEffect(() => {
		if (!lightboxOpen) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setLightboxOpen(false);
		};
		window.addEventListener("keydown", onKey);
		return () => {
			document.body.style.overflow = prev;
			window.removeEventListener("keydown", onKey);
		};
	}, [lightboxOpen]);

	return (
		<section className="relative overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white py-20 sm:py-24">
			{/* Background ornaments */}
			<div
				className="pointer-events-none absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full blur-3xl"
				style={{
					background: `radial-gradient(circle, ${PRIMARY}1a, transparent 70%)`,
				}}
			/>
			<div
				className="pointer-events-none absolute -bottom-40 -right-32 h-[480px] w-[480px] rounded-full blur-3xl"
				style={{
					background: `radial-gradient(circle, ${ACCENT}1a, transparent 70%)`,
				}}
			/>

			<div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
				{/* Section eyebrow */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.5 }}
					className="mb-3 flex items-center gap-3">
					<span
						className="h-px w-10"
						style={{ background: PRIMARY }}
					/>
					<span
						className="text-[11px] font-semibold uppercase tracking-[0.3em]"
						style={{ color: PRIMARY }}>
						{eyebrow}
					</span>
				</motion.div>

				<motion.h2
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.55, delay: 0.05 }}
					className="text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-slate-900">
					{title}
				</motion.h2>

				{/* HERO GRID */}
				<div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10 lg:items-stretch">
					{/* LEFT — 3D Tilt visual */}
					<div
						className="lg:col-span-7 flex flex-col"
						style={{ perspective: 1400 }}>
						{images.length > 0 && (
							<div className="flex-1 min-h-0">
								<TiltShowcaseCard
									image={images[safeIndex]}
									imageAlt={imageAlt}
									wordmarkPrimary={wordmarkPrimary}
									wordmarkSecondary={wordmarkSecondary}
									compliancePill={compliancePill}
									captionEyebrow={captionEyebrow}
									captionTitle={captionTitle}
									onImageClick={() => setLightboxOpen(true)}
								/>
							</div>
						)}

						{/* Thumbnails */}
						{images.length > 1 && (
							<div
								className="mt-4 grid shrink-0 gap-3"
								style={{
									gridTemplateColumns: `repeat(${Math.min(
										images.length,
										4,
									)}, minmax(0, 1fr))`,
								}}>
								{images.map((src, i) => (
									<button
										key={`${src}-${i}`}
										type="button"
										onClick={() => setActiveImage(i)}
										aria-label={`Show product ${i + 1}`}
										className={`group relative h-20 sm:h-24 overflow-hidden rounded-xl border bg-white transition-all duration-300 ${
											safeIndex === i ?
												"border-[#3b0092] shadow-[0_8px_20px_rgba(59,0,146,0.25)]"
											:	"border-slate-200 hover:border-slate-400"
										}`}>
										<img
											src={src}
											alt=""
											className="absolute inset-0 h-full w-full object-contain p-2 transition-transform duration-500 group-hover:scale-110"
											onError={(e) => {
												(e.currentTarget as HTMLImageElement).src =
													"/noimg.png";
											}}
										/>
										{safeIndex === i && (
											<span
												className="absolute bottom-1 right-1 h-1.5 w-1.5 rounded-full"
												style={{ background: ACCENT }}
											/>
										)}
									</button>
								))}
							</div>
						)}
					</div>

					{/* RIGHT — Description + highlights */}
					<div className="lg:col-span-5 flex flex-col gap-5">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.55 }}
							className="text-base leading-relaxed text-slate-700">
							{description}
						</motion.div>

						{highlights.length > 0 && (
							<motion.div
								initial={{ opacity: 0, y: 30 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true, margin: "-50px" }}
								transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
								className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_8px_24px_rgba(15,23,42,0.06)]">
								{highlights.map((h, i) => {
									const Icon = h.icon;
									return (
										<div
											key={h.title}
											className={`group flex items-start gap-4 p-5 transition-colors duration-300 hover:bg-[#3b0092]/3 ${
												i !== 0 ? "border-t border-slate-100" : ""
											}`}>
											<div
												className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-105"
												style={{
													background: `linear-gradient(135deg, ${PRIMARY} 0%, #5a1bd1 100%)`,
												}}>
												<Icon className="h-5 w-5 text-white" />
											</div>
											<div className="flex-1">
												<h4 className="text-base font-semibold text-slate-900">
													{h.title}
												</h4>
												<p className="mt-1 text-sm leading-relaxed text-slate-600">
													{h.body}
												</p>
											</div>
										</div>
									);
								})}
								<div
									className="pointer-events-none absolute right-0 top-0 h-20 w-20"
									style={{
										background: `radial-gradient(circle at top right, ${ACCENT}30, transparent 70%)`,
									}}
								/>
							</motion.div>
						)}
					</div>
				</div>

				{/* PERFORMANCE SPECIFICATIONS */}
				{specs.length > 0 && (
					<div className="mt-20 sm:mt-24">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.55 }}
							className="mb-3 flex items-center gap-3">
							<span
								className="h-px w-10"
								style={{ background: ACCENT }}
							/>
							<span
								className="text-[11px] font-semibold uppercase tracking-[0.3em]"
								style={{ color: ACCENT }}>
								{specsEyebrow}
							</span>
						</motion.div>
						<motion.h3
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.55, delay: 0.05 }}
							className="text-2xl sm:text-3xl font-semibold text-slate-900">
							{specsTitle}
						</motion.h3>

						<div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
							{/* Header row — a 4/6 column split leaves the feature cell about
							    60px wide on a phone, so below `sm` the rows stack and this
							    header is redundant. */}
							<div
								className="hidden grid-cols-12 divide-x divide-white/15 text-[11px] font-semibold uppercase tracking-[0.22em] text-white sm:grid"
								style={{
									background: `linear-gradient(90deg, ${PRIMARY} 0%, #5a1bd1 100%)`,
								}}>
								<div className="col-span-4 px-5 py-4">
									{t("common.ui.feature")}
								</div>
								<div className="col-span-6 px-5 py-4">
									{t("common.ui.capability")}
								</div>
							</div>

							{/* Body rows */}
							<div className="divide-y divide-slate-200">
								{specs.map(({ icon: Icon, feature, capability }, i) => (
									<motion.div
										key={feature}
										initial={{ opacity: 0, x: -20 }}
										whileInView={{ opacity: 1, x: 0 }}
										viewport={{ once: true, margin: "-30px" }}
										transition={{
											duration: 0.45,
											delay: i * 0.05,
											ease: [0.22, 1, 0.36, 1],
										}}
										className={`group relative flex flex-col transition-colors duration-300 hover:bg-[#3b0092]/3 sm:grid sm:grid-cols-12 sm:items-stretch sm:divide-x sm:divide-slate-200 ${
											i % 2 === 1 ? "bg-slate-50/60" : "bg-white"
										}`}>
										<span
											className="pointer-events-none absolute top-2 bottom-2 left-0 w-[3px] origin-top scale-y-0 rounded-r-full transition-transform duration-300 group-hover:scale-y-100"
											style={{ background: ACCENT }}
										/>

										<div className="flex items-center px-4 pt-3 sm:col-span-4 sm:px-5 sm:py-4">
											<span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#3b0092] sm:text-sm sm:normal-case sm:tracking-normal sm:text-slate-900">
												{feature}
											</span>
										</div>

										<div className="flex items-center px-4 pb-3 pt-1 sm:col-span-6 sm:px-5 sm:py-4">
											<span className="text-sm leading-relaxed text-slate-700">
												{capability}
											</span>
										</div>
									</motion.div>
								))}
							</div>

							{/* Footer caption strip */}
						</div>
					</div>
				)}
			</div>

			{/* Lightbox preview — portaled to body so it ignores any
				transformed/perspective ancestors and centers on the viewport */}
			{typeof document !== "undefined" &&
				createPortal(
					<AnimatePresence>
						{lightboxOpen && images.length > 0 && (
							<motion.div
								key="ache-lightbox"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.25 }}
								onClick={() => setLightboxOpen(false)}
								className="fixed inset-0 z-100 flex items-center justify-center bg-black/75 p-6 backdrop-blur-md"
								role="dialog"
								aria-modal="true"
								aria-label={t("common.ui.productImagePreview")}>
								<motion.img
									key={images[safeIndex]}
									initial={{ scale: 0.85, opacity: 0 }}
									animate={{ scale: 1, opacity: 1 }}
									exit={{ scale: 0.9, opacity: 0 }}
									transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
									src={images[safeIndex]}
									alt={imageAlt}
									onClick={(e) => e.stopPropagation()}
									className="max-h-[90vh] max-w-[92vw] object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.55)]"
									onError={(e) => {
										(e.currentTarget as HTMLImageElement).src = "/noimg.png";
									}}
								/>
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										setLightboxOpen(false);
									}}
									aria-label={t("common.ui.closePreview")}
									className="absolute right-6 top-6 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur-md transition hover:bg-white/20">
									<X className="h-5 w-5" />
								</button>
							</motion.div>
						)}
					</AnimatePresence>,
					document.body,
				)}
		</section>
	);
};

export default AcheShowcase;
